import { searchLocation } from '@/services/locationService';
import { ILocation, ISchedulerLocation } from '@/services/types';
import clsx from 'clsx';
import debounce from 'lodash.debounce';
import { useEffect, useState } from 'react';
import React from 'react';

import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import TextField from '@material-ui/core/TextField';
import { Autocomplete, AutocompleteRenderInputParams } from '@material-ui/lab';

import styles from './LocationAutoComplete.module.scss';

interface LocationOptionProps {
  option: ILocation;
}
interface LocationInputProps {
  params: AutocompleteRenderInputParams;
  placeholder: string;
  onFocus: () => void;
  onChange: (value: string) => void;
  onBlur: () => void;
}

export interface LocationAutoCompleteProps {
  value: ILocation | ISchedulerLocation | null;
  onChange: (value: ILocation | ISchedulerLocation | null) => void;
  className?: string;
  placeholder?: string;
  label?: string;
}

const LocationOption = ({ option }: LocationOptionProps) => (
  <Box className={clsx(styles.option, option.divider && styles.divider)}>
    <div className={styles.optionContent}>
      <div className={styles.locationName}>{option.name}</div>
      <div className={styles.locationSubtitle}>
        {'city' in option
          ? [option.city, option.state, option.country].filter(Boolean).join(', ')
          : option.country}
      </div>
    </div>
  </Box>
);

const LocationInput = ({ params, placeholder, onFocus, onChange, onBlur }: LocationInputProps) => (
  <TextField
    {...params}
    placeholder={placeholder}
    InputProps={{
      ...params.InputProps,
      disableUnderline: true,
    }}
    onFocus={onFocus}
    onChange={e => onChange(e.target.value)}
    onBlur={onBlur}
  />
);

export const LocationAutoComplete: React.FC<LocationAutoCompleteProps> = ({
  value,
  onChange,
  className,
  placeholder = 'Where...',
  label = 'Location',
}) => {
  const [locations, setLocations] = useState<(ILocation | ISchedulerLocation)[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [openOptions, setOpenOptions] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSearchTermChange = debounce((term: string) => setSearchTerm(term), 500);

  useEffect(() => {
    if (searchTerm.length < 2) {
      setOpenOptions(false);
      return;
    }

    setLocations([]);
    setLoading(true);
    setOpenOptions(true);

    const fetchLocations = async () => {
      try {
        const data = await searchLocation(searchTerm);
        // Map scheduler locations with divider flag, then regular locations
        const mappedSchedulerLocations = data.locations.map(
          (loc, idx) =>
            ({
              ...loc,
              divider: idx === data.locations.length - 1,
            } as ISchedulerLocation),
        );
        const combinedLocations = [...mappedSchedulerLocations, ...data.results];
        setLocations(combinedLocations);
        setLoading(false);
      } catch (error) {
        setLocations([]);
        setLoading(false);
        setOpenOptions(false);
      }
    };

    fetchLocations();
  }, [searchTerm]);

  return (
    <div className={clsx(styles.container, className)}>
      <label className={styles.label}>{label}</label>
      <Autocomplete
        value={value}
        onChange={(_event, newValue) => {
          onChange(newValue);
          setOpenOptions(false);
        }}
        options={locations}
        open={openOptions}
        loading={loading}
        autoHighlight
        filterOptions={options => options}
        popupIcon={<></>} // Remove the dropdown icon
        clearOnBlur
        getOptionLabel={(option: ILocation | ISchedulerLocation) => option.name}
        getOptionSelected={(option, value) => option.name === value.name}
        ListboxProps={{
          style: { maxHeight: 300, overflow: 'auto' },
        }}
        loadingText={
          <Box display="flex" justifyContent="center" alignItems="center" style={{ gap: 8 }}>
            <CircularProgress size={20} />
            <span>Searching...</span>
          </Box>
        }
        noOptionsText={
          <div className={styles.noOptions}>No locations found for "{searchTerm}"</div>
        }
        renderOption={option => <LocationOption option={option} />}
        renderInput={params => (
          <LocationInput
            params={params}
            placeholder={placeholder}
            onFocus={() => {
              setOpenOptions(false);
              setSearchTerm('');
            }}
            onChange={onSearchTermChange}
            onBlur={() => setOpenOptions(false)}
          />
        )}
      />
    </div>
  );
};
